import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { StarsInput } from "@/components/coachly/StarRating";
import { coachlyApi } from "@/lib/api";
import { COACH_PROFILE } from "@/constants/testIds";
import { PenLine, X } from "lucide-react";

export default function ReviewForm({ slug, onCreated }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const reset = () => {
    setRating(0);
    setEmail("");
    setName("");
    setComment("");
    setStatus({ state: "idle", message: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      setStatus({ state: "error", message: "Please pick a star rating (1–5)." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus({ state: "error", message: "Please enter a valid email address." });
      return;
    }
    if (!name.trim()) {
      setStatus({ state: "error", message: "Please enter your name." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      const res = await coachlyApi.createReview(slug, {
        client_email: email.trim(),
        client_name: name.trim(),
        rating,
        comment: comment.trim() || null,
      });
      toast.success("Review posted. Thanks for sharing.");
      setStatus({ state: "success", message: "Your review is live." });
      onCreated?.(res.review);
      setTimeout(() => {
        setOpen(false);
        reset();
      }, 900);
    } catch (err) {
      const message = err?.response?.data?.detail || "Something went wrong. Try again.";
      setStatus({ state: "error", message });
      toast.error(message);
    }
  };

  return (
    <div>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={COACH_PROFILE.reviewFormOpen}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13.5px] font-semibold transition-colors"
        >
          <PenLine className="w-4 h-4" strokeWidth={2.2} />
          Write a review
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.form
            initial={{ opacity: 0, y: 14, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -6, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={submit}
            data-testid={COACH_PROFILE.reviewForm}
            className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
          >
            <div className="p-6 md:p-7">
              <div className="flex items-start justify-between">
                <div>
                  <div className="eyebrow">§ Your review</div>
                  <h4 className="mt-2 font-display text-2xl tracking-tight text-slate-900">
                    How was your experience?
                  </h4>
                  <p className="mt-1 text-[13px] text-slate-500 max-w-[52ch]">
                    Reviews are open to clients who've completed a session with this coach. Yours will be public on their profile.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setOpen(false); reset(); }}
                  className="p-2 -m-2 text-slate-400 hover:text-slate-900 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-6">
                <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500">Rating</label>
                <div className="mt-2">
                  <StarsInput value={rating} onChange={setRating} testIdPrefix="review-form-star" />
                </div>
              </div>

              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500">Your name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid={COACH_PROFILE.reviewFormName}
                    placeholder="e.g. Sam T."
                    className="mt-2 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition"
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500">Email (waitlist)</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={COACH_PROFILE.reviewFormEmail}
                    placeholder="you@domain.com"
                    className="mt-2 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition"
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500">
                  Comment <span className="normal-case tracking-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid={COACH_PROFILE.reviewFormComment}
                  rows={4}
                  maxLength={1200}
                  placeholder="What worked, what to expect, who this coach is right for…"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25 focus:border-[#2563EB]/40 transition resize-y"
                />
              </div>

              <div className="mt-6 flex items-center justify-between gap-4">
                <p className="text-[11.5px] text-slate-400">
                  By posting, you confirm you had a real session with this coach.
                </p>
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid={COACH_PROFILE.reviewFormSubmit}
                  className="h-11 px-6 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60"
                >
                  {status.state === "loading" ? "Posting…" : "Post review"}
                </button>
              </div>

              {status.state === "success" && (
                <div data-testid={COACH_PROFILE.reviewFormSuccess} className="mt-4 text-[13px] text-[#10B981]">
                  {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div data-testid={COACH_PROFILE.reviewFormError} className="mt-4 text-[13px] text-red-500">
                  {status.message}
                </div>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
